import {compose, identity, thrush as match} from '@packages/utilities/fp/utils';
import {Err, Ok, either, Result} from '@packages/utilities/fp/result';
import type {RelationshipConstraint} from '@packages/systems/constraints-types';
import {weakMemo} from '@packages/utilities/memo';
import {type Option, Some, None, maybe} from '@packages/utilities/fp/option';
import {getPluginName} from '@packages/systems/wfdl-Qualified';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';

export type RelationshipConstraintErrorDetails = {
  on: {
    displayName: string;
  };
  against: {
    displayName: string;
    count: number;
  };
  constraint: RelationshipConstraint;
};

export type AtMostNConstraintErrorDetails = {
  on: {
    displayName: string;
  };
  against: {
    displayName: string;
    count: number;
  };
  constraint: Extract<RelationshipConstraint, {rule: 'AtMostN'}>;
};

type ConstraintErrorCode<a> = (arg1: RelationshipConstraintErrorDetails) => a;
type AtMostNConstraintErrorCode<a> = (arg1: AtMostNConstraintErrorDetails) => a;

type Mapping<a> = {
  RequiresSingleItemSelection: a;
  ElementMissing: a;
  LiveTreeMissing: a;
  OneInstanceRequired: a;
  OneInstancePerComponent: a;
  RequiredOnPage: a;
  SymbolsCircularReferences: a;
  UndeletableUserField: a;
  UnmovableElement: (arg1: ExpressionElement) => a;
  ContainsRequiredElement: a;
  CanvasNotFocused: a;
  RequiredAncestor: ConstraintErrorCode<a>;
  RequiredExactlyAncestor: ConstraintErrorCode<a>;
  ForbidAncestor: ConstraintErrorCode<a>;
  RequiredDescendant: ConstraintErrorCode<a>;
  RequiredExactlyDescendant: ConstraintErrorCode<a>;
  ForbidDescendant: ConstraintErrorCode<a>;
  RequiredZeroOrOneDescendant: ConstraintErrorCode<a>;
  RequiredZeroOrOneAncestor: ConstraintErrorCode<a>;
  RequiredAtMostNAncestor: AtMostNConstraintErrorCode<a>;
  RequiredAtMostNDescendant: AtMostNConstraintErrorCode<a>;
};

export type ErrorCode = <a>(arg1: Mapping<a>) => a;

export const RequiresSingleItemSelection: ErrorCode = <a>(
  mapping: Mapping<a>
): a => mapping.RequiresSingleItemSelection;
export const ElementMissing: ErrorCode = <a>(mapping: Mapping<a>): a =>
  mapping.ElementMissing;
export const LiveTreeMissing: ErrorCode = <a>(mapping: Mapping<a>): a =>
  mapping.LiveTreeMissing;
export const OneInstanceRequired: ErrorCode = <a>(mapping: Mapping<a>): a =>
  mapping.OneInstanceRequired;
export const OneInstancePerComponent: ErrorCode = <a>(mapping: Mapping<a>): a =>
  mapping.OneInstancePerComponent;
export const RequiredOnPage: ErrorCode = <a>(mapping: Mapping<a>): a =>
  mapping.RequiredOnPage;
export const SymbolsCircularReferences: ErrorCode = <a>(
  mapping: Mapping<a>
): a => mapping.SymbolsCircularReferences;
export const UndeletableUserField: ErrorCode = <a>(mapping: Mapping<a>): a =>
  mapping.UndeletableUserField;
export const UnmovableElement: (arg1: ExpressionElement) => ErrorCode =
  (element) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.UnmovableElement(element);
export const ContainsRequiredElement: ErrorCode = <a>(mapping: Mapping<a>): a =>
  mapping.ContainsRequiredElement;
export const CanvasNotFocused: ErrorCode = <a>(mapping: Mapping<a>): a =>
  mapping.CanvasNotFocused;

export const RequiredAncestor: ConstraintErrorCode<ErrorCode> =
  (details) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.RequiredAncestor(details);

export const ForbidAncestor: ConstraintErrorCode<ErrorCode> =
  (details) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.ForbidAncestor(details);

export const RequiredExactlyAncestor: ConstraintErrorCode<ErrorCode> =
  (details) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.RequiredExactlyAncestor(details);

export const RequiredDescendant: ConstraintErrorCode<ErrorCode> =
  (details) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.RequiredDescendant(details);

export const ForbidDescendant: ConstraintErrorCode<ErrorCode> =
  (details) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.ForbidDescendant(details);

export const RequiredExactlyDescendant: ConstraintErrorCode<ErrorCode> =
  (details) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.RequiredExactlyDescendant(details);

export const RequiredZeroOrOneDescendant: ConstraintErrorCode<ErrorCode> =
  (details) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.RequiredZeroOrOneDescendant(details);

export const RequiredZeroOrOneAncestor: ConstraintErrorCode<ErrorCode> =
  (details) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.RequiredZeroOrOneAncestor(details);

export const RequiredAtMostNAncestor: AtMostNConstraintErrorCode<ErrorCode> =
  (details) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.RequiredAtMostNAncestor(details);

export const RequiredAtMostNDescendant: AtMostNConstraintErrorCode<ErrorCode> =
  (details) =>
  <a>(mapping: Mapping<a>): a =>
    mapping.RequiredAtMostNDescendant(details);

type Message = Option<string>;

export type MessageGetter = (err: ErrorCode) => Message;

type ErrorData = Option<RelationshipConstraintErrorDetails>;
export type ErrorDataGetter = (err: ErrorCode) => ErrorData;

type MessageGettersByKey = {
  RequiredAncestor: ConstraintErrorCode<Message>;
  RequiredExactlyAncestor: ConstraintErrorCode<Message>;
  ForbidAncestor: ConstraintErrorCode<Message>;
  RequiredDescendant: ConstraintErrorCode<Message>;
  RequiredExactlyDescendant: ConstraintErrorCode<Message>;
  ForbidDescendant: ConstraintErrorCode<Message>;
  RequiredZeroOrOneDescendant: ConstraintErrorCode<Message>;
  RequiredZeroOrOneAncestor: ConstraintErrorCode<Message>;
  RequiredAtMostNAncestor: AtMostNConstraintErrorCode<Message>;
  RequiredAtMostNDescendant: AtMostNConstraintErrorCode<Message>;
};

const messageGetterByKey = (
  action: keyof NonNullable<RelationshipConstraint['messages']>,
  defaults: MessageGettersByKey
): MessageGettersByKey =>
  Object.keys(defaults).reduce((memo, key) => {
    memo[key] = (details: any) => {
      if (details.constraint.messages && details.constraint.messages[action]) {
        return Some(details.constraint.messages[action]);
      }
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'MessageGettersByKey'.
      return defaults[key](details);
    };

    return memo;
  }, {} as any);

// @ts-expect-error - TS2322 - Type '<B>(arg1: (arg2: { RequiredAncestor: ConstraintErrorCode<Message>; RequiredExactlyAncestor: ConstraintErrorCode<Message>; ... 15 more ...; UnmovableElement: () => Option<...>; }) => B) => B' is not assignable to type 'MessageGetter'.
export const getCutMessage: MessageGetter = match({
  RequiresSingleItemSelection: Some(
    'Cut is not supported for multiple elements'
  ),
  ElementMissing: Some('There is no node selected to cut.'),
  LiveTreeMissing: Some('The Live Tree is missing'),
  CanvasNotFocused: None,
  OneInstanceRequired: Some(
    'There can only be one instance of this element on the page.'
  ),
  OneInstancePerComponent: Some(
    'There needs to be one instance of this element in this component.'
  ),
  ContainsRequiredElement: Some(
    'This element contains an element that cannot be cut.'
  ),
  RequiredOnPage: Some(
    'This component is required on this page and cannot be deleted.'
  ),
  SymbolsCircularReferences: Some(
    `Components cannot have circular references.`
  ),
  UndeletableUserField: Some(
    'This is a bound user field. Use the setting panel to add or remove these.'
  ),
  UnmovableElement: () => None,
  ...messageGetterByKey('cut', {
    RequiredAncestor: (_) => None,
    ForbidAncestor: (_) => None,
    RequiredExactlyAncestor: (_) => None,
    RequiredDescendant: ({on: ancestor, against: descendant}) =>
      Some(
        `${descendant.displayName} can not be cut from its ${ancestor.displayName}.`
      ),
    RequiredExactlyDescendant: ({on: ancestor, against: descendant}) =>
      Some(
        `${descendant.displayName} can not be cut from its ${ancestor.displayName}.`
      ),
    ForbidDescendant: (_) => None,
    RequiredZeroOrOneDescendant: (_) => None,
    RequiredZeroOrOneAncestor: (_) => None,
    RequiredAtMostNAncestor: (_) => None,
    RequiredAtMostNDescendant: (_) => None,
  }),
});

// @ts-expect-error - TS2322 - Type '<B>(arg1: (arg2: { RequiredAncestor: ConstraintErrorCode<Message>; RequiredExactlyAncestor: ConstraintErrorCode<Message>; ... 15 more ...; UnmovableElement: () => Option<...>; }) => B) => B' is not assignable to type 'MessageGetter'.
export const getRemoveMessage: MessageGetter = match({
  RequiresSingleItemSelection: Some(
    'Deletion is not supported for multiple elements'
  ),
  ElementMissing: Some('There is no node selected to delete.'),
  LiveTreeMissing: Some('The Live Tree is missing'),
  CanvasNotFocused: None,
  OneInstanceRequired: Some(
    'There needs to be one instance of this element on the page.'
  ),
  OneInstancePerComponent: Some(
    'There needs to be one instance of this element in this component.'
  ),
  ContainsRequiredElement: Some(
    'This element contains an element that cannot be deleted.'
  ),
  RequiredOnPage: Some(
    'This component is required on this page and cannot be deleted.'
  ),
  SymbolsCircularReferences: Some(
    `Components cannot have circular references.`
  ),
  UndeletableUserField: Some(
    'This is a bound user field. Use the setting panel to add or remove these.'
  ),
  UnmovableElement: () => None,
  ...messageGetterByKey('remove', {
    RequiredAncestor: (_) => None,
    ForbidAncestor: (_) => None,
    RequiredExactlyAncestor: (_) => None,
    RequiredDescendant: ({on: ancestor, against: descendant}) =>
      Some(
        `${descendant.displayName} can not be removed from its ${ancestor.displayName}.`
      ),
    RequiredExactlyDescendant: ({on: ancestor, against: descendant}) =>
      Some(
        `${descendant.displayName} can not be removed from its ${ancestor.displayName}.`
      ),
    ForbidDescendant: (_) => None,
    RequiredZeroOrOneDescendant: (_) => None,
    RequiredZeroOrOneAncestor: (_) => None,
    RequiredAtMostNAncestor: (_) => None,
    RequiredAtMostNDescendant: (_) => None,
  }),
});

// @ts-expect-error - TS2322 - Type '<B>(arg1: (arg2: { RequiredAncestor: ConstraintErrorCode<Message>; RequiredExactlyAncestor: ConstraintErrorCode<Message>; ... 15 more ...; UnmovableElement: () => Option<...>; }) => B) => B' is not assignable to type 'MessageGetter'.
export const getCopyMessage: MessageGetter = match({
  RequiresSingleItemSelection: Some(
    'Copy is not supported for multiple elements'
  ),
  ElementMissing: Some('There is no node selected to copy.'),
  LiveTreeMissing: Some('The Live Tree is missing'),
  CanvasNotFocused: None,
  OneInstanceRequired: Some(
    'There can only be one instance of this element on the page.'
  ),
  OneInstancePerComponent: Some(
    'There can only be one instance of this element in this component.'
  ),
  ContainsRequiredElement: Some(
    'This element contains an element that cannot be copied.'
  ),
  RequiredOnPage: None,
  SymbolsCircularReferences: Some(
    `Components cannot have circular references.`
  ),
  UndeletableUserField: Some(
    'This is a bound user field. Use the setting panel to add or remove these.'
  ),
  UnmovableElement: () => None,
  ...messageGetterByKey('copy', {
    RequiredAncestor: (_) => None,
    ForbidAncestor: (_) => None,
    RequiredExactlyAncestor: (_) => None,
    RequiredDescendant: (_) => None,
    RequiredExactlyDescendant: (_) => None,
    ForbidDescendant: (_) => None,
    RequiredZeroOrOneDescendant: (_) => None,
    RequiredZeroOrOneAncestor: (_) => None,
    RequiredAtMostNAncestor: (_) => None,
    RequiredAtMostNDescendant: (_) => None,
  }),
});

// @ts-expect-error - TS2322 - Type '<B>(arg1: (arg2: { RequiredAncestor: ConstraintErrorCode<Message>; RequiredExactlyAncestor: ConstraintErrorCode<Message>; ... 15 more ...; UnmovableElement: () => Option<...>; }) => B) => B' is not assignable to type 'MessageGetter'.
export const getDuplicateMessage: MessageGetter = match({
  ElementMissing: Some('There is no node selected to duplicate.'),
  LiveTreeMissing: Some('The Live Tree is missing'),
  CanvasNotFocused: None,
  OneInstanceRequired: Some(
    'There can only be one instance of this element on the page.'
  ),
  OneInstancePerComponent: Some(
    'There can only be one instance of this element in this component.'
  ),
  ContainsRequiredElement: Some(
    'This element contains an element that cannot be duplicated.'
  ),
  RequiredOnPage: None,
  SymbolsCircularReferences: Some(
    `Components cannot have circular references.`
  ),
  UndeletableUserField: Some(
    'This is a bound user field. Use the setting panel to add or remove these.'
  ),
  UnmovableElement: () => None,
  ...messageGetterByKey('duplicate', {
    RequiredAncestor: (_) => None,
    ForbidAncestor: (_) => None,
    RequiredExactlyAncestor: (_) => None,
    RequiredDescendant: (_) => None,
    RequiredExactlyDescendant: (_) => None,
    ForbidDescendant: (_) => None,
    RequiredZeroOrOneDescendant: ({on: ancestor, against: {displayName}}) => {
      return Some(
        `${ancestor.displayName} can not have more than one ${displayName}.`
      );
    },
    RequiredZeroOrOneAncestor: (_) => None,
    RequiredAtMostNAncestor: (_) => None,
    RequiredAtMostNDescendant: ({
      on: ancestor,
      against: {displayName},
      constraint: {maxCount},
    }) => {
      return Some(
        `${ancestor.displayName} can not have more than ${maxCount} ${displayName} elements.`
      );
    },
  }),
});

// @ts-expect-error - TS2322 - Type '<B>(arg1: (arg2: { RequiredAncestor: ConstraintErrorCode<Message>; RequiredExactlyAncestor: ConstraintErrorCode<Message>; ... 15 more ...; UnmovableElement: () => Option<...>; }) => B) => B' is not assignable to type 'MessageGetter'.
export const getMoveMessage: MessageGetter = match({
  ElementMissing: None,
  LiveTreeMissing: None,
  CanvasNotFocused: None,
  OneInstanceRequired: None,
  OneInstancePerComponent: Some(
    'There can only be one instance of this element in this component.'
  ),
  ContainsRequiredElement: None,
  RequiredOnPage: None,
  SymbolsCircularReferences: Some(
    `Components cannot have circular references.`
  ),
  UndeletableUserField: None,
  UnmovableElement: () => None,
  ...messageGetterByKey('move', {
    RequiredAncestor: ({on: descendant, against: ancestor}) =>
      Some(
        `${descendant.displayName} can only be placed in a ${ancestor.displayName}.`
      ),
    ForbidAncestor: ({on: descendant, against: ancestor}) =>
      descendant.displayName === ancestor.displayName
        ? Some(
            `${descendant.displayName} elements can not be nested inside each other.`
          )
        : Some(
            `${descendant.displayName} can not be placed in a ${ancestor.displayName}.`
          ),
    RequiredExactlyAncestor: ({
      on: descendant,
      against: {count, displayName},
    }) => {
      if (count > 1) {
        return Some(
          `${displayName} can only have one ${descendant.displayName}.`
        );
      } else {
        return Some(
          `${descendant.displayName} can only be placed in a ${displayName}.`
        );
      }
    },
    RequiredDescendant: ({on: ancestor, against: descendant}) =>
      Some(
        `${descendant.displayName} can not be placed out of its ${ancestor.displayName}.`
      ),
    RequiredExactlyDescendant: ({
      on: ancestor,
      against: {count, displayName},
    }) => {
      if (count > 1) {
        return Some(
          `${ancestor.displayName} can not contain more than one ${displayName} element.`
        );
      } else {
        return Some(
          `${ancestor.displayName} can not contain less than one ${displayName} element.`
        );
      }
    },
    ForbidDescendant: ({on: ancestor, against: descendant}) =>
      descendant.displayName === ancestor.displayName
        ? Some(
            `${ancestor.displayName} elements can not be nested inside each other.`
          )
        : Some(
            `${descendant.displayName} can not be placed in a ${ancestor.displayName}.`
          ),

    RequiredZeroOrOneAncestor: ({on: descendant, against: {displayName}}) => {
      return Some(
        `${descendant.displayName} can only be nested in one ${displayName}.`
      );
    },

    RequiredZeroOrOneDescendant: ({on: ancestor, against: {displayName}}) => {
      return Some(
        `${ancestor.displayName} can not have more than one ${displayName}.`
      );
    },

    RequiredAtMostNAncestor: ({
      on: descendant,
      against: {displayName},
      constraint: {maxCount},
    }) => {
      return Some(
        `${descendant.displayName} can only be nested in up to ${maxCount} ${displayName} elements.`
      );
    },

    RequiredAtMostNDescendant: ({
      on: ancestor,
      against: {displayName},
      constraint: {maxCount},
    }) => {
      return Some(
        `${ancestor.displayName} can not have more than ${maxCount} ${displayName} elements.`
      );
    },
  }),
});

// @ts-expect-error - TS2322 - Type '<B>(arg1: (arg2: { RequiredAncestor: ConstraintErrorCode<Message>; RequiredExactlyAncestor: ConstraintErrorCode<Message>; ... 15 more ...; UnmovableElement: () => Option<...>; }) => B) => B' is not assignable to type 'MessageGetter'.
export const getPasteMessage: MessageGetter = match({
  ElementMissing: None,
  LiveTreeMissing: None,
  CanvasNotFocused: None,
  OneInstanceRequired: None,
  OneInstancePerComponent: None,
  ContainsRequiredElement: None,
  RequiredOnPage: None,
  SymbolsCircularReferences: Some(
    'Components cannot have circular references.'
  ),
  UndeletableUserField: None,
  UnmovableElement: () => None,
  ...messageGetterByKey('paste', {
    RequiredAncestor: ({on: descendant, against: ancestor}) =>
      Some(
        `${descendant.displayName} can only be pasted in a ${ancestor.displayName}.`
      ),
    ForbidAncestor: ({on: descendant, against: ancestor}) =>
      descendant.displayName === ancestor.displayName
        ? Some(
            `${descendant.displayName} elements can not be pasted inside each other.`
          )
        : Some(
            `${descendant.displayName} can not be pasted in a ${ancestor.displayName}.`
          ),
    RequiredExactlyAncestor: ({
      on: descendant,
      against: {count, displayName},
    }) => {
      if (count > 1) {
        return Some(
          `${displayName} can only have one ${descendant.displayName}.`
        );
      } else {
        return Some(
          `${descendant.displayName} can only be pasted in a ${displayName}.`
        );
      }
    },
    RequiredDescendant: ({on: ancestor, against: descendant}) =>
      Some(
        `${descendant.displayName} can not be pasted out of a ${ancestor.displayName}.`
      ),
    RequiredExactlyDescendant: ({
      on: ancestor,
      against: {count, displayName},
    }) => {
      if (count > 1) {
        return Some(
          `${ancestor.displayName} can only contain one ${displayName} element.`
        );
      } else {
        return Some(
          `${ancestor.displayName} can not contain less than one ${displayName} element.`
        );
      }
    },
    ForbidDescendant: ({on: ancestor, against: descendant}) =>
      ancestor.displayName === descendant.displayName
        ? Some(
            `${ancestor.displayName} elements can not be pasted inside each other.`
          )
        : Some(
            `${descendant.displayName} can not be pasted in a ${ancestor.displayName}.`
          ),

    RequiredZeroOrOneAncestor: ({on: descendant, against: {displayName}}) => {
      return Some(
        `${descendant.displayName} can only be nested in one ${displayName}.`
      );
    },

    RequiredZeroOrOneDescendant: ({on: ancestor, against: {displayName}}) => {
      return Some(
        `${ancestor.displayName} can not contain more than one ${displayName}.`
      );
    },

    RequiredAtMostNAncestor: ({
      on: descendant,
      against: {displayName},
      constraint: {maxCount},
    }) => {
      return Some(
        `${descendant.displayName} can only be nested in up to ${maxCount} ${displayName} elements.`
      );
    },

    RequiredAtMostNDescendant: ({
      on: ancestor,
      against: {displayName},
      constraint: {maxCount},
    }) => {
      return Some(
        `${ancestor.displayName} can not contain more than ${maxCount} ${displayName} elements.`
      );
    },
  }),
});

const serializeAncestorRule =
  (type: string) =>
  // @ts-expect-error - TS7031 - Binding element 'descendant' implicitly has an 'any' type. | TS7031 - Binding element 'ancestor' implicitly has an 'any' type.
  ({on: descendant, against: ancestor}) =>
    `${type}(${descendant.displayName}, ${ancestor.displayName})`;
const serializeDescendantRule =
  (type: string) =>
  // @ts-expect-error - TS7031 - Binding element 'ancestor' implicitly has an 'any' type. | TS7031 - Binding element 'descendant' implicitly has an 'any' type.
  ({on: ancestor, against: descendant}) =>
    `${type}(${ancestor.displayName}, ${descendant.displayName})`;

// @ts-expect-error - TS2322 - Type '<B>(arg1: (arg2: { ElementMissing: string; LiveTreeMissing: string; CanvasNotFocused: string; OneInstanceRequired: string; OneInstancePerComponent: string; ContainsRequiredElement: string; SymbolsCircularReferences: string; ... 10 more ...; RequiredZeroOrOneAncestor: ({ on: descendant, against: ancestor }: { ...; })...' is not assignable to type '(err: ErrorCode) => string'.
export const toString: (err: ErrorCode) => string = match({
  ElementMissing: 'ElementMissing',
  LiveTreeMissing: 'LiveTreeMissing',
  CanvasNotFocused: 'CanvasNotFocused',
  OneInstanceRequired: 'OneInstanceRequired',
  OneInstancePerComponent: 'OneInstancePerComponent',
  ContainsRequiredElement: 'ContainsRequiredElement',
  RequiredOnPage: 'RequiredOnPage',
  SymbolsCircularReferences: 'SymbolsCircularReferences',
  UndeletableUserField: 'UndeletableUserField',
  UnmovableElement: (element) => {
    return `Unmovable(${getPluginName(element.type) || 'Element'})`;
  },
  RequiredAncestor: serializeAncestorRule('RequiredAncestor'),
  ForbidAncestor: serializeAncestorRule('ForbidAncestor'),
  RequiredExactlyAncestor: serializeAncestorRule('RequiredExactlyAncestor'),
  RequiredDescendant: serializeDescendantRule('RequiredDescendant'),
  RequiredExactlyDescendant: serializeDescendantRule(
    'RequiredExactlyDescendant'
  ),
  ForbidDescendant: serializeDescendantRule('ForbidDescendant'),
  RequiredZeroOrOneDescendant: serializeDescendantRule(
    'RequiredZeroOrOneDescendant'
  ),
  RequiredZeroOrOneAncestor: serializeAncestorRule('RequiredZeroOrOneAncestor'),
  RequiredAtMostNAncestor: serializeAncestorRule('RequiredAtMostNAncestor'),
  RequiredAtMostNDescendant: serializeDescendantRule(
    'RequiredAtMostNDescendant'
  ),
});

export const errorCodeToMessage = weakMemo(
  (
    messageGetter: MessageGetter
  ): (<A>(res: Result<ErrorCode, A>) => Result<string, A>) =>
    either(
      compose<Message, Result<string, any>>(maybe(Err(`Can't do this`))(Err))(
        messageGetter
      )
    )(Ok)
);

export const errorCodeToData = weakMemo(<a>(errorDataGetter: ErrorDataGetter) =>
  either<ErrorCode, null | RelationshipConstraintErrorDetails>(
    compose(
      maybe<null | RelationshipConstraintErrorDetails>(null)(
        identity<RelationshipConstraintErrorDetails>
      )
    )(errorDataGetter)
  )((_: a) => null)
);
